import { FC, useEffect, useRef } from 'react';
import { useForm, SubmitHandler, Controller } from 'react-hook-form';
import cx from 'classnames';
import UserProfileImage from '../../UserProfileImage/UserProfileImage';
import { AutoSizeTextArea } from '../../AutoSizeTextArea/AutoSizeTextArea';
import { TCloudinaryImageShape } from '../../CloudinaryResponsiveImage/types';
import PostCommentButton from './PostCommentButton';
import DiscardCommentButton from './DiscardCommentButton';

type TCreateCommentFormProps = {
  commenterProfileImage: TCloudinaryImageShape;
  onSubmit: (commentValue: string) => void;
  isLoading?: boolean;
  isVisible?: boolean;
  onDiscardButtonClick?: () => void;
  showCancelButton?: boolean;
  autofocusWhenVisible?: boolean;
};

type TCreateCommentFormValuesProps = {
  commentValue: string;
};

const CreateCommentForm: FC<TCreateCommentFormProps> = ({
  commenterProfileImage,
  onSubmit,
  isLoading,
  onDiscardButtonClick,
  isVisible,
  showCancelButton = false,
  autofocusWhenVisible = false,
}) => {
  const textAreaRef = useRef<any>(null);

  const {
    control,
    handleSubmit,
    reset,
    formState: { isValid },
  } = useForm<TCreateCommentFormValuesProps>({
    defaultValues: {
      commentValue: '',
    },
    mode: 'onChange',
  });

  const isCommentValueProvided = (commentValue: string) => commentValue?.match('^\\s*[\\S]') !== null;

  const handleOnSubmit: SubmitHandler<TCreateCommentFormValuesProps> = async (data) => {
    await onSubmit(data.commentValue);
    reset();
  };

  const onSubmitCallback = handleSubmit(handleOnSubmit);

  const handleOnDiscardButtonClick = () => {
    onDiscardButtonClick?.();
    reset();
  };

  useEffect(() => {
    if (isVisible && autofocusWhenVisible) {
      textAreaRef.current?.focus();
    }
  }, [isVisible, autofocusWhenVisible]);

  return (
    <div
      className={cx('flex gap-x-4 bg-white-900 rounded-[8px] transition', {
        'p-6 opacity-1 duration-700': isVisible,
        'px-6 mt-2 pb-0 opacity-0 duration-200 h-0 pointer-events-none': !isVisible,
      })}
    >
      <div className="w-fit">
        <UserProfileImage size="medium" profileImage={commenterProfileImage} />
      </div>
      <form className="relative w-full" onSubmit={onSubmitCallback}>
        <Controller
          name="commentValue"
          control={control}
          render={({ field }) => (
            <AutoSizeTextArea
              className={cx(
                'w-full flex pr-2 pl-7 pt-3 pb-10 flex-col gap-y-3 font-secondary !outline-t2-neutral-grey05',
                'overflow-y-hidden',
                {
                  'min-h-[100px]': isVisible,
                },
              )}
              placeholder="What’s on your mind"
              rows={3}
              {...field}
              ref={(node) => {
                field.ref(node);
                textAreaRef.current = node;
              }}
            />
          )}
          rules={{ validate: { isCommentValueProvided } }}
        />
        {showCancelButton && <DiscardCommentButton onClick={handleOnDiscardButtonClick} />}
        <PostCommentButton isLoading={isLoading} isValid={isValid} />
      </form>
    </div>
  );
};
export default CreateCommentForm;
